import React from 'react';
import { mapValues, noop, omit } from 'lodash';
import PropTypes from 'prop-types';
import { nucleusFormShape } from '../NucleusForm';
import missingNucleusFormError from '../utils/missingNucleusFormError';
import shallowObjectEqual from '../utils/shallowObjectEqual';
import storeShape from '../utils/storeShape';
import Subscription from '../utils/Subscription';
import { validate } from '../utils/validate';
const wrapInputWithValidations = options => Component => {
    var _a;
    const displayName = `InputValidations(${Component.displayName})`;
    const { storeKey = 'store' } = options;
    return _a = class Validation extends React.Component {
            constructor(props, context) {
                super(props, context);
                this.updateSubscription = props => {
                    const { validations, mapStateToValidationContext } = props;
                    const shouldSubscribe = validations && mapStateToValidationContext;
                    if (shouldSubscribe) {
                        this.subscription.trySubscribe();
                    }
                    else {
                        this.subscription.tryUnsubscribe();
                    }
                };
                this.getContext = () => {
                    const { mapStateToValidationContext, validationContext } = this.props;
                    const state = this.store ? this.store.getState() : undefined;
                    return mapStateToValidationContext ? mapStateToValidationContext(state, validationContext) : validationContext;
                };
                this.contextualValidator = value => {
                    const { validations } = this.props;
                    return validate(validations, value, this.getContext());
                };
                this.validate = (value, context) => {
                    const { [options.inputIdKey]: inputId, validations } = this.props;
                    this.nucleusForm.actions.validateInput(inputId, validations, value, context || this.getContext());
                };
                this.handleStateChange = () => {
                    const { [options.valueKey]: value } = this.props;
                    const context = this.getContext();
                    if (!shallowObjectEqual(context, this.validationContext)) {
                        this.validationContext = context;
                        this.validate(value, context);
                    }
                };
                this.isValidationEvent = (validationEvents, eventType) => {
                    if (!eventType) {
                        return false;
                    }
                    return validationEvents instanceof Array ? validationEvents.includes(eventType) : validationEvents === eventType;
                };
                this.createHandleEvent = (eventOptions, eventName) => {
                    return (...args) => {
                        const { [eventName]: onEvent = noop, validateOn, [options.valueKey]: value } = this.props;
                        onEvent.apply(void 0, args);
                        if (this.isValidationEvent(validateOn, eventOptions.eventType)) {
                            this.validate(value);
                        }
                    };
                };
                this.eventHandlers = mapValues(options.events, this.createHandleEvent);
                this.nucleusForm = props.nucleusForm || context.nucleusForm;
                if (!this.nucleusForm) {
                    throw missingNucleusFormError(displayName);
                }
                this.store = props[storeKey] || context[storeKey];
                this.subscription = new Subscription(this.store, this.handleStateChange);
                const { [options.inputIdKey]: inputId, [options.valueKey]: value } = props;
                this.nucleusForm.actions.addInput(inputId);
                this.nucleusForm.actions.setInputValidations(inputId, this.contextualValidator);
                this.validate(value);
                this.updateSubscription(props);
            }
            componentDidUpdate(prevProps) {
                const { [options.valueKey]: oldValue, validationContext: oldContext } = prevProps;
                const { [options.inputIdKey]: inputId, [options.valueKey]: currentValue, validationContext: currentContext, validateOn } = this.props;
                const valueChanged = oldValue !== currentValue;
                const validateOnValueChange = valueChanged && this.isValidationEvent(validateOn, 'change');
                if (validateOnValueChange || oldContext !== currentContext) {
                    this.validate(currentValue);
                }
                else if (valueChanged) {
                    this.nucleusForm.actions.setInputValidationValue(inputId, currentValue);
                }
                this.updateSubscription(this.props);
            }
            componentWillUnmount() {
                this.nucleusForm.actions.removeInput(this.props[options.inputIdKey]);
            }
            render() {
                const validationOnlyProps = ['validateOn', 'validations', 'validationContext', 'mapStateToValidationContext'];
                return React.createElement(Component, Object.assign({}, omit(this.props, validationOnlyProps), this.eventHandlers), this.props.children);
            }
        },
        _a.displayName = displayName,
        _a.contextTypes = {
            nucleusForm: nucleusFormShape,
            [storeKey]: storeShape
        },
        _a.propTypes = {
            [options.inputIdKey]: PropTypes.string.isRequired,
            validateOn: PropTypes.oneOfType([PropTypes.oneOf(['change', 'focus', 'blur']), PropTypes.arrayOf(PropTypes.oneOf(['change', 'focus', 'blur']))]),
            validations: PropTypes.objectOf(PropTypes.shape({
                validator: PropTypes.func.isRequired
            })),
            validationContext: PropTypes.any,
            mapStateToValidationContext: PropTypes.func,
            children: PropTypes.node,
            nucleusForm: nucleusFormShape,
            [storeKey]: storeShape
        },
        _a.defaultProps = {
            validateOn: options.defaultValidateOn
        },
        _a;
};
export default wrapInputWithValidations;
